import { icons } from 'assets/icons';
import logo from 'assets/images/navenio-landscape.svg';
import logoSmall from 'assets/images/navenio-small.svg';
import IconButton from 'components/Button/IconButton';
import UserInfoDropdown from 'components/Header/components/UserInfoDropdown/UserInfoDropdown';
import { useUserRolePermissionStore } from 'contexts/RootStore.context';
import { Horizontal, StretchSpacer, Vertical } from 'gls';
import useDevice from 'hooks/useDevice';
import React, { useMemo, useState } from 'react';
import { configRoutePath } from 'router/routePaths';
import CollapseButton from './CollapseButton';
import { navItemsType } from './Navigation';
import NavLinkItem from './NavLinkItem';
import { StyledAside, StyledMobileMenu, StyledNav, StyledTop } from './styled';
import useNavHooks from './useNavHooks';

type ConfigNavigationProps = {
  navCollapsedDefault?: boolean
}
const ConfigNavigation = ({ navCollapsedDefault = false }: ConfigNavigationProps): React.ReactElement => {
  const [userHasCollapsed, setUserHasCollapsed] = useState(false);
  const [navIsCollapsed, setNavIsCollapsed] = useState(userHasCollapsed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isTabletDevice } = useDevice();
  const { canViewWorkgroupRoles, canManageWorkgroupRoles, canManageGroupsAndRoles, canViewConfig } = useUserRolePermissionStore()

  const navItems: navItemsType[] = useMemo(() => {
    const navItems: navItemsType[] = []
    if (canViewConfig) {
      navItems.push(
        {
          name: 'System Administrators',
          href: configRoutePath.systemAdminList,
          icon: icons.SystemAdministratorsIcon,
        },
        {
          name: 'User Management',
          href: '',
          icon: icons.UserManagementIcon,
          submenu: [{
            name: 'View Users',
            href: configRoutePath.userManagement,
          }, {
            name: 'Import History',
            href: configRoutePath.importHistory,
          }]
        },
        {
          name: 'Workgroups',
          href: configRoutePath.Workgroups,
          icon: icons.WorkgroupsIcon,
        },
        {
          name: 'Roles',
          href: '',
          icon: icons.RolesIcon,
          submenu: [{
            name: 'Create Role',
            hidden: !canManageGroupsAndRoles,
            href: configRoutePath.addRole,
          }, {
            name: 'View and Manage Roles',
            href: configRoutePath.listRoles,
          }]
        },
      )
    }
    else if (canViewWorkgroupRoles || canManageWorkgroupRoles) {
      const subMenus = canManageWorkgroupRoles
        ? [{
          name: 'Create Role',
          href: configRoutePath.addRole,
        }, {
          name: 'View and Manage Roles',
          href: configRoutePath.listRoles,
        }]
        : [{
          name: 'View and Manage Roles',
          href: configRoutePath.listRoles,
        }]
      navItems.push({
        name: 'Roles',
        href: '',
        icon: icons.RolesIcon,
        submenu: subMenus,
      })
    }
    return navItems;

  }, [canManageGroupsAndRoles, canManageWorkgroupRoles, canViewConfig, canViewWorkgroupRoles]);

  const { toggleNavOpen, activeHref, setActiveHref } = useNavHooks({
    userHasCollapsed,
    navCollapsedDefault,
    navIsCollapsed,
    setNavIsCollapsed,
    setUserHasCollapsed,
  });

  if (mobileMenuOpen) {
    return (
      <StyledMobileMenu>
        <Vertical className="container" horizontalAlign="left" spacing={10}>
          <IconButton
            className="nav-close-button"
            icon={<icons.CloseIcon aria-label='Close Menu' width={15} height={15} />}
            onClick={() => setMobileMenuOpen(false)}
            ariaLabel="Close Menu"
          />
          <div className="nav-userinfo">
            <UserInfoDropdown onItemClick={() => setMobileMenuOpen(false)} />
          </div>
          <div className="nav-seperator"></div>
          <StyledNav>
            <ul className="nav-menu-list">
              {navItems.filter(link => !link.hidden).map((link, index) => {
                return (
                  <NavLinkItem
                    key={`link-${index}`}
                    link={link}
                    navIsCollapsed={false}
                    activeHref={activeHref}
                    setActiveHref={setActiveHref}
                    setNavIsCollapsed={setNavIsCollapsed}
                    setUserHasCollapsed={setUserHasCollapsed}
                    closeMobileMenu={() => setMobileMenuOpen(false)}
                  />
                );
              })}
            </ul>
          </StyledNav>
          <StretchSpacer />
        </Vertical>
      </StyledMobileMenu>
    );
  }

  if (isTabletDevice) {
    return (
      <StyledTop>
        <Horizontal spacing="auto" className="nav-container" horizontalAlign="center">
          <IconButton
            id="mobile-nav-button"
            icon={<icons.MobileMenu aria-label='Open Menu' width={22} height={22} />}
            onClick={() => setMobileMenuOpen(true)}
            ariaLabel="Open Menu"
          />
          <img src={logo} height="35px" alt="Navenio Logo" />
          <div className="nav-search">&nbsp;</div>
        </Horizontal>
      </StyledTop>
    );
  }

  return (
    <StyledAside className={navIsCollapsed ? '--collapsed' : ''}>
      <div className="nav-container">
        <div className="logo-container">
          <img src={navIsCollapsed ? logoSmall : logo} alt="Navenio Logo" />
        </div>

        <CollapseButton onClick={toggleNavOpen} />

        <StyledNav>
          <ul className="nav-menu-list">
            {navItems.map((link, index) => {
              return (
                <NavLinkItem
                  key={`link-${index}`}
                  link={link}
                  navIsCollapsed={navIsCollapsed}
                  activeHref={activeHref}
                  setActiveHref={setActiveHref}
                  setNavIsCollapsed={setNavIsCollapsed}
                  setUserHasCollapsed={setUserHasCollapsed}
                />
              );
            })}
          </ul>
        </StyledNav>
      </div>
    </StyledAside>
  );
};
export default ConfigNavigation;
